// src/hooks/useAIUsageLimit.ts

import { useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { SubscriptionInfo, AIUsageDocument } from '../types/user';

/**
 * AI 功能類型定義（與後端保持一致）
 */
export enum AIFeature {
  AI_PLANNING = 'aiPlanning',
  CAPTURE_ANALYSIS = 'captureAnalysis', 
  SMART_SUGGESTIONS = 'smartSuggestions',
  DATE_SUMMARY = 'dateSummary',
  DEFAULT = 'default'
}

/**
 * 使用量狀態介面
 */
export interface AIUsageStatus {
  used: number;
  limit: number | null;
  remaining: number | null;
  isUnlimited: boolean;
}

/**
 * Hook 返回值介面
 */
export interface UseAIUsageLimitReturn {
  // 狀態
  isLoading: boolean;
  error: string | null;
  
  // 訂閱資訊
  subscription: SubscriptionInfo | null;
  
  // 使用量狀態
  usageStats: Record<AIFeature, AIUsageStatus>;
  
  // 方法
  getFeatureStatus: (feature: AIFeature) => AIUsageStatus;
  canUseFeature: (feature: AIFeature) => boolean;
  getRemainingUses: (feature: AIFeature) => number | null;
  refreshUsage: () => void;
  
  // 升級引導
  showUpgradeModal: (feature?: AIFeature) => void;
}

/**
 * AI 使用量限制管理 Hook (v2.0)
 * 
 * 重要更新：
 * - 監聽獨立的 ai_usage collection 代替 users.aiUsage
 * - 移除前端重置邏輯，由後端懶加載處理
 * - 提高性能，避免監聽用戶主文檔的無關更新
 * 
 * 功能：
 * - 監聽用戶的 AI 使用狀態
 * - 計算各功能的剩餘使用次數
 * - 提供升級引導
 * - 只讀取狀態，不修改使用量（由後端控制）
 */
export const useAIUsageLimit = (): UseAIUsageLimitReturn => {
  const { currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [aiUsage, setAIUsage] = useState<AIUsageDocument | null>(null);
  
  // 功能限制配置（與後端邏輯一致）
  const getFeatureLimit = useCallback((feature: AIFeature): number => {
    // 這些值應該與後端的 aiUsageManager.js 中的配置保持一致
    // 開發環境相對寬鬆，生產環境嚴格
    const isDevelopment = process.env.NODE_ENV !== 'production';
    
    const limits: Record<AIFeature, number> = {
      [AIFeature.AI_PLANNING]: isDevelopment ? 10 : 1,
      [AIFeature.CAPTURE_ANALYSIS]: isDevelopment ? 20 : 5,
      [AIFeature.SMART_SUGGESTIONS]: isDevelopment ? 15 : 3,
      [AIFeature.DATE_SUMMARY]: isDevelopment ? 15 : 3,
      [AIFeature.DEFAULT]: isDevelopment ? 10 : 3
    };
    
    return limits[feature] || limits[AIFeature.DEFAULT];
  }, []);
  
  // 分別監聽用戶訂閱狀態和 AI 使用量（新架構）
  useEffect(() => {
    if (!currentUser) {
      setIsLoading(false);
      setSubscription(null);
      setError(null);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    // 監聽用戶訂閱狀態
    const userDocRef = doc(db, 'users', currentUser.uid);
    const userUnsubscribe = onSnapshot(
      userDocRef,
      (docSnapshot) => {
        try {
          if (docSnapshot.exists()) {
            const userData = docSnapshot.data();
            setSubscription(userData.subscription || {
              plan: 'free',
              status: 'none'
            });
          } else {
            setSubscription({
              plan: 'free',
              status: 'none'
            });
          }
        } catch (err) {
          console.error('解析訂閱狀態失敗:', err);
          setError('Failed to load subscription status');
        }
      },
      (err) => {
        console.error('監聽訂閱狀態失敗:', err);
        setError('Failed to monitor subscription status');
      }
    );
    
    return () => userUnsubscribe();
  }, [currentUser]);
  
  // 獨立監聽 AI 使用量
  useEffect(() => {
    if (!currentUser) {
      setAIUsage(null);
      return;
    }
    
    // 監聽 AI 使用量（獨立 collection）
    const aiUsageDocRef = doc(db, 'ai_usage', currentUser.uid);
    const aiUsageUnsubscribe = onSnapshot(
      aiUsageDocRef,
      (docSnapshot) => {
        try {
          if (docSnapshot.exists()) {
            const aiUsageData = docSnapshot.data() as AIUsageDocument;
            setAIUsage(aiUsageData);
          } else {
            // 首次使用，預設為空狀態
            setAIUsage(null);
          }
          
          setIsLoading(false);
          setError(null);
        } catch (err) {
          console.error('解析 AI 使用量狀態失敗:', err);
          setError('Failed to load AI usage status');
          setIsLoading(false);
        }
      },
      (err) => {
        console.error('監聽 AI 使用量失敗:', err);
        setError('Failed to monitor AI usage status');
        setIsLoading(false);
      }
    );
    
    return () => aiUsageUnsubscribe();
  }, [currentUser]);
  
  // 計算使用量統計（新架構：移除前端重置邏輯）
  const usageStats = useCallback((): Record<AIFeature, AIUsageStatus> => {
    const stats: Record<AIFeature, AIUsageStatus> = {} as any;
    
    // 檢查是否是無限制用戶
    const isUnlimited = subscription?.status === 'trialing' || subscription?.plan === 'pro';
    
    // 新架構：不再在前端處理重置，直接使用後端資料
    const today = new Date().toISOString().split('T')[0];
    const needsReset = aiUsage?.lastResetDate !== today;
    
    Object.values(AIFeature).forEach(feature => {
      // 如果需要重置或沒有資料，預設為 0
      const currentUsage = (needsReset || !aiUsage) ? 0 : (aiUsage.daily[feature] || 0);
      const limit = getFeatureLimit(feature);
      const remaining = isUnlimited ? null : Math.max(0, limit - currentUsage);
      
      stats[feature] = {
        used: currentUsage,
        limit: isUnlimited ? null : limit,
        remaining,
        isUnlimited
      };
    });
    
    return stats;
  }, [subscription, aiUsage, getFeatureLimit]);
  
  // 獲取特定功能的狀態
  const getFeatureStatus = useCallback((feature: AIFeature): AIUsageStatus => {
    const stats = usageStats();
    return stats[feature];
  }, [usageStats]);
  
  // 檢查是否可以使用特定功能
  const canUseFeature = useCallback((feature: AIFeature): boolean => {
    const status = getFeatureStatus(feature);
    return status.isUnlimited || (status.remaining !== null && status.remaining > 0);
  }, [getFeatureStatus]);
  
  // 獲取剩餘使用次數
  const getRemainingUses = useCallback((feature: AIFeature): number | null => {
    const status = getFeatureStatus(feature);
    return status.remaining;
  }, [getFeatureStatus]);
  
  // 刷新使用量（新架構：簡化刷新邏輯）
  const refreshUsage = useCallback(() => {
    // 新架構使用 Firebase realtime listeners，會自動同步
    // 如果需要強制刷新，可以在這裡添加邏輯
  }, []);
  
  // 顯示升級 Modal（由父組件實作具體邏輯）
  const showUpgradeModal = useCallback((feature?: AIFeature) => {
    // 這裡可以觸發全局狀態或事件來顯示升級 Modal
    // 具體實作會在後續的 Modal 組件中處理
    
    // 可以發送自定義事件
    window.dispatchEvent(new CustomEvent('show-upgrade-modal', {
      detail: { feature, trigger: 'usage-limit' }
    }));
  }, []);
  
  return {
    isLoading,
    error,
    subscription,
    usageStats: usageStats(),
    getFeatureStatus,
    canUseFeature,
    getRemainingUses,
    refreshUsage,
    showUpgradeModal
  };
};